/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Evgeniy Naumov
 * @version 1.22.0
 * @since 1.19.0
 */

import { Fragment, useMemo } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import Title from '@components/common/Title/Title';
import { EBasketType } from '@modules/app/appConstants';
import withAsyncStateManagement from '@modules/asyncStateManagment/WithAsyncStateManagement';
import PageLocale from '@modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '@modules/internationalization/constants';
import { makeSelectTranslationWizardMultiSearch } from '@modules/internationalization/intSelectors';
import { IWizardMultiSearchTranslation } from '@modules/internationalization/types';
import { ECommonTranslationPages } from '@modules/internationalization/types/common/pages';
import Wizard from '@modules/wizard/components/Wizard';
import { EWizardName } from '@modules/wizard/wizardConstants';
import { IWizardState } from '@modules/wizard/wizardReducer';
import { makeSelectWizardStepName } from '@modules/wizard/wizardSelectors';
import { IRootReducer } from '@reducers/rootReducer';

import { wizardMultiSearchHandleNextAction } from '../actions';
import { EWizardMultiSearchSteps } from '../constant';
import wizardMultiSearchReducer, { IWizardMultiSearchReducer } from '../reducer';
import { makeSelectWizardMultiSearchValidation } from '../selectors';
import { WizardMultiSearchStyled } from './styled/WizardMultiSearchStyled';
import WizardMultiSearchStepConfirm from './WizardMultiSearchStepConfirm';
import WizardMultiSearchStepSelection from './WizardMultiSearchStepSelection';
import WizardMultiSearchTitleElements from './WizardMultiSearchTitleElements';

const WizardMultiSearch = ({
                               handleNext,
                               stepName,
                               validation,
                               wizardTranslation,
                           }: IConnectedProps & IDispatchProps) => {

    const Step = useMemo(() => {
        switch (stepName) {
            case EWizardMultiSearchSteps.Selection:
                return <WizardMultiSearchStepSelection/>;
            case EWizardMultiSearchSteps.Confirm:
                return <WizardMultiSearchStepConfirm/>;
        }
    }, [stepName]);

    const { ConfirmSelectionStep } = wizardTranslation

    return (
        <Fragment>
            <PageLocale lazy
                        fileNames={[
                            TranslationFileNamesEnum.wizardMultiSearch,
                            TranslationFileNamesEnum.wizard,
                        ]}/>

            <Title pageName={ECommonTranslationPages.wizardMultiSearch}/>

            <Wizard
                basketType={EBasketType.MOBILE}
                isNextDisabled={!validation?.isValid}
                name={EWizardName.MULTI_SEARCH}
                titleElements={
                    <WizardMultiSearchTitleElements btnExportErrorTitle={ConfirmSelectionStep.exportButton}/>
                }
                translationProps={{
                    error: wizardTranslation?.error,
                    steps: wizardTranslation?.steps,
                    title: wizardTranslation?.title,
                }}
                validateError={validation?.error}
                onWizardNextHandler={handleNext}
            >
                <WizardMultiSearchStyled>
                    {Step}
                </WizardMultiSearchStyled>
            </Wizard>

        </Fragment>
    );
};

interface IConnectedProps extends Pick<IWizardState, 'stepName'>, Pick<IWizardMultiSearchReducer, 'validation'> {
    wizardTranslation: IWizardMultiSearchTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    stepName: makeSelectWizardStepName(),
    validation: makeSelectWizardMultiSearchValidation(),
    wizardTranslation: makeSelectTranslationWizardMultiSearch(),
});

export interface IDispatchProps {
    handleNext: () => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    handleNext: () => dispatch(wizardMultiSearchHandleNextAction()),
});

export default withAsyncStateManagement(
    connect(mapStateToProps, mapDispatchToProps)(WizardMultiSearch),
    { reducer: { wizardMultiSearch: wizardMultiSearchReducer } },
);
